import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, AlertCircle, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0776BD] via-[#055A92] to-[#044A7A] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src="/mr-electric-logo.jpg" alt="Mr. Electric" className="h-24 w-auto object-contain rounded-lg bg-white p-2 shadow-lg" />
          </div>
          <p className="text-white/70 mt-1">Inventory Control</p>
        </div>

        <Card className="border-0 shadow-xl rounded-xl">
          {sent ? (
            <CardContent className="pt-8 pb-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle2 size={32} className="text-green-600" />
              </div>
              <h2 className="font-heading text-2xl text-[#1F2937] mb-2" data-testid="forgot-success-title">Check Your Email</h2>
              <p className="text-[#6B7280] mb-2">
                If <span className="font-medium text-[#1F2937]">{email}</span> is registered, a password reset link has been sent.
              </p>
              <p className="text-sm text-[#9CA3AF] mb-6">
                The link expires in 1 hour. Check your spam folder if you don't see it.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full bg-[#0776BD] hover:bg-[#055A92] text-white rounded-lg"
                  data-testid="back-to-login-from-success"
                >
                  Back to Sign In
                </Button>
                <button
                  onClick={() => { setSent(false); setEmail(''); }}
                  className="text-sm text-[#6B7280] hover:text-[#0776BD] hover:underline"
                  data-testid="try-different-email"
                >
                  Try a different email
                </button>
              </div>
            </CardContent>
          ) : (
            <>
              <CardHeader className="space-y-1 pb-4">
                <div className="flex items-center gap-2">
                  <Mail size={22} className="text-[#0776BD]" />
                  <CardTitle className="font-heading text-2xl tracking-tight text-[#1F2937]">Forgot Password</CardTitle>
                </div>
                <CardDescription className="text-[#6B7280]">
                  Enter your email and we'll send you a link to reset your password.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {error && (
                  <Alert variant="destructive" className="mb-4 rounded-lg">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-[#1F2937]">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@mrelectric.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="forgot-email-input"
                      className="h-12 rounded-lg border-gray-300 focus:border-[#0776BD] focus:ring-[#0776BD]/20"
                      autoFocus
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-[#0776BD] hover:bg-[#055A92] text-white font-semibold rounded-lg shadow-sm"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>
                    ) : (
                      'Send Reset Link'
                    )}
                  </Button>
                </form>

                <div className="mt-4 text-center">
                  <button
                    onClick={() => navigate('/login')}
                    className="text-sm text-[#6B7280] hover:text-[#0776BD] hover:underline inline-flex items-center gap-1"
                    data-testid="back-to-login-link"
                  >
                    <ArrowLeft size={14} /> Back to Sign In
                  </button>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
